import { object, string, InferType, date } from 'yup'
import { useFormContext } from '../context/FormContext'
import {  TextField,  Box, Autocomplete } from '@mui/material'
import { useMemo } from 'react'
import countries_en from '../labels/countries_en'
import countries_de from '../labels/countries_de'
import { formatDateString_en } from './PositionScreen'

export const aboutSchema = object({
    firstName: string().required('Bitte gib einen Vornamen an.'),
    lastName: string().required('Bitte gib einen Nachnamen an.'),
    birthdate: date(),
    pronouns: string(),
    email: string().email('Die E-Mail-Adresse ist üngultig.').required('Bitte gib eine E-Mail-Adresse an.'),
    phone: string().required('Bitte gib eine Telefonnummer an.'),
    nationality: string().required('Bitte gib eine Nationalität an.'),
    residency: string().required('Bitte gib einen Wohnort an.'),
})

export const aboutSchema_en = object({
    firstName: string().required('Please fill in your first name.'),
    lastName: string().required('Please fill in your last name.'),
    birthdate: date(),
    pronouns: string(),
    email: string().email('Invalid email address.').required('Please fill in your email address.'),
    phone: string().required('Please fill in your phone number.'),
    nationality: string().required('Please fill in your nationality.'),
    residency: string().required('Please provide your residency.'),
})

export interface AboutData extends Omit<InferType<typeof aboutSchema>, 'birthdate'> {
    birthdate: Date | '',
}

const AboutScreen = () => {

    const {
        lang, formData, formik, labels
    } = useFormContext()

    const countries = useMemo(() => {
        if (lang === 'de') 
            return countries_de
        return countries_en
    }, [lang])

    const {
        firstName, lastName, birthdate, pronouns, email, phone, nationality, residency
    } = formData

    if (!formik) {
        return null
    }
    
    const { handleChange, handleBlur, touched, errors, setFieldValue } = formik

    return (
        <Box width="100%" className="screen-about">
            <div className="react-form-title">
                <h2>{labels.about.title}</h2>
            </div>
            <Box sx={{ flex: 1, }}>
            <TextField 
                id="firstName"
                inputProps={{maxLength: 20}}
                label={labels.about.firstName}
                value={firstName}
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName && errors.firstName}
                name='firstName'
                variant="standard"
                sx={{width: '100%', '@media (min-width: 1024px)': {width: 'calc(50% - 20px)', }}}
                
            />
            <TextField 
                id="lastName"
                inputProps={{ maxLength: 30 }}
                label={labels.about.lastName}
                value={lastName}
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName && errors.lastName}
                name='lastName'
                variant="standard"
                sx={{ width: '100%', '@media (min-width: 1024px)': {width: 'calc(50% - 20px)', ml:'40px' } }}
            />
            <TextField
                id="birthdate"
                label={labels.about.birthdate}
                value={birthdate ? formatDateString_en((new Date(birthdate))) : ''}
                onFocus={(e) => { const input = e.target as HTMLInputElement; input.type = 'date'; input.pattern = "yyyy-MM-dd"; input.focus(); input.pattern = "yyyy-MM-dd"; input.showPicker(); }}
                onBlur={(e) => { const input = e.target as HTMLInputElement; input.type = 'text'; handleBlur(e) }}
                onChange={handleChange}
                error={touched.birthdate && Boolean(errors.birthdate)}
                helperText={touched.birthdate && errors.birthdate && String(errors.birthdate)}
                name='birthdate'
                variant="standard"
                fullWidth
                sx={{ width: '100%', '@media (min-width: 1024px)': { width: 'calc(50% - 20px)', } }}
            />
            
            <TextField 
                id="pronouns"
                inputProps={{ maxLength: 20 }}
                label={labels.about.pronouns}
                value={pronouns}
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.pronouns && Boolean(errors.pronouns)}
                helperText={touched.pronouns && errors.pronouns}
                name='pronouns'
                variant="standard"
                sx={{ width: '100%', '@media (min-width: 1024px)': { width: 'calc(50% - 20px)', ml: '40px' } }}
            />
            <TextField 
                id="email"
                inputProps={{ maxLength: 80 }}
                label={labels.about.email}
                value={email}
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                name='email'
                variant="standard"
                    sx={{ width: '100%', '@media (min-width: 1024px)': { width: 'calc(50% - 20px)' } }}
            />
            <TextField 
                id="phone"
                inputProps={{ maxLength: 40 }}
                label={labels.about.phone}
                value={phone}
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.phone && Boolean(errors.phone)}
                helperText={touched.phone && errors.phone}
                name='phone'
                variant="standard"
                sx={{ width: '100%', '@media (min-width: 1024px)': { width: 'calc(50% - 20px)', ml: '40px' } }}
            />
            <TextField
                id="residency"
                inputProps={{ maxLength: 25 }}
                label={labels.about.residency}
                value={residency}
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.residency && Boolean(errors.residency)}
                helperText={touched.residency && errors.residency}
                name='residency'
                variant="standard"
                    sx={{ width: '100%', '@media (min-width: 1024px)': { width: 'calc(50% - 20px)'  } }}
            />
            <Autocomplete
                id='nationality'
                options={['', ...Object.entries(countries).sort((a, b) => a[1].localeCompare(b[1])).map(e => e[1])]}
                onChange={(e, value) => {setFieldValue('nationality', value)}}
                value={nationality}
                onBlur={handleBlur}
                sx={{ width: '100%', '@media (min-width: 1024px)': { width: 'calc(50% - 20px)', maxWidth: '606px', } }}
                componentsProps={{
                    clearIndicator: {
                        style: {
                            // You can adjust the size here
                            height: '28px',
                            width: '28px',
                        },
                        sx: {
                            '& svg': {
                            fontSize: '24px',
                            },
                        },
                    },
                    popupIndicator: {
                        style: {
                            // Adjust the size as needed
                            height: '28px',
                            width: '28px',
                        },
                        sx: {
                            '& svg': {
                                fontSize: '24px',
                            },
                        },
                    },
                    
                }}
                renderInput={(params) => 
                    <TextField {...params} 
                        label={labels.about.nationality} 
                        variant="standard" 
                        error={touched.nationality && Boolean(errors.nationality)}
                        helperText={touched.nationality && errors.nationality}
                        
                     />}
            />


            {/* <FormControl fullWidth sx={{height: '70px'}}>
                <InputLabel id="nationality-label" 
                    sx={{ 
                        transform: (touched.nationality || Boolean(nationality)) ? 'scale(0.75)' : 'scale(1) translate(0, 19px)',
                        color: touched.nationality && Boolean(errors.nationality) ? 'var(--error)' : 'rgba(0, 0, 0, 0.6)',
                    }}
                    >{labels.about.nationality}</InputLabel>
                <Select
                    id="nationality"
                    label={labels.about.nationality}
                    labelId="nationality-label"
                    value={nationality}  
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.nationality && Boolean(errors.nationality)}
                    name='nationality'
                    variant="standard"
                    fullWidth
                    >
                        {
                        Object.entries(countries).sort((a, b) => a[1].localeCompare(b[1])).map(([countryCode, countryName], index) => {
                            return <MenuItem key={index} value={countryCode}>{countryName}</MenuItem>
                        })
                        }
                </Select>
                <FormHelperText sx={{mx:0, transform: 'unset', color: 'var(--error)'}} >{touched.nationality && errors.nationality}</FormHelperText>
            </FormControl> */}

            </Box>
        </Box>
    )
}

export default AboutScreen